import React, { FC } from "react";

import { Box, Flex, HStack } from "@chakra-ui/react";
import { i18n, TFunction, Trans, useTranslation } from "next-i18next";

import Image from "@components/Image";
import responsiveStyle from "@lib/theme/responsiveStyle";
import joinDefinedStringParts from "@utils/joinDefinedStringParts";

import { DeliveryMethod } from "../../constants";
import type { TPaymentStatus } from "../../types";

type TOrderInfoProps = {
  paymentStatus: TPaymentStatus;
};

const getDeliveryMethodTitle = (t: TFunction, deliveryMethod: DeliveryMethod): string => {
  const titlesMap = {
    [DeliveryMethod.PudoToPudo]: t("order-delivery_method-pudo_pudo"),
    [DeliveryMethod.PudoToDoor]: t("order-delivery_method-pudo_door"),
    [DeliveryMethod.DoorToDoor]: t("order-delivery_method-door_door"),
    [DeliveryMethod.DoorToPudo]: t("order-delivery_method-door_pudo"),
  };
  return titlesMap[deliveryMethod] || "";
};

const OrderInfo: FC<TOrderInfoProps> = ({ paymentStatus }) => {
  const { t } = useTranslation();

  const { deliveryMethod, packageSize, parcelNo, pin, pickupDate, pickupTimeFrom, pickupTimeTill } = paymentStatus;

  const pinInfo = pin && (
    <Box
      display={responsiveStyle({ mobile: "table-caption", desktop: "block" })}
      fontSize={responsiveStyle({ mobile: "0.875rem", desktop: "1rem" })}
      lineHeight="1.2"
    >
      <Trans i18n={i18n!} i18nKey="order-success-pin" values={{ pin }} />
    </Box>
  );

  const pickupInfo = pickupDate && (
    <Box lineHeight="1.2" fontSize={responsiveStyle({ mobile: "0.875rem", desktop: "1rem" })}>
      <Box fontWeight="300">{t("order-success-pickup_time")}</Box>
      <Box fontWeight="400">
        {pickupDate} {joinDefinedStringParts([pickupTimeFrom, pickupTimeTill], " - ")}
      </Box>
    </Box>
  );

  return (
    <HStack spacing="1rem" borderTop="1px solid" borderBottom="1px solid" borderColor="grey.light" py="0.5rem">
      <Flex width="3.125rem" height="1.75rem" justify="center" align="center" position="relative">
        <Image src="/assets/images/package.png" alt="" layout="fill" />
      </Flex>
      <Box flex="1">
        <Box fontSize="1.125rem" lineHeight="1.2">
          {deliveryMethod && getDeliveryMethodTitle(t, deliveryMethod)}
        </Box>
        <Flex>
          <Box flex="1">
            <Box fontWeight="300" lineHeight="1.2" fontSize={responsiveStyle({ mobile: "0.875rem", desktop: "1rem" })}>
              {packageSize && <Trans i18n={i18n!} i18nKey="order-success-parcel_size" values={{ size: packageSize }} />}
            </Box>
            <Box lineHeight="1.2" fontSize={responsiveStyle({ mobile: "0.875rem", desktop: "1rem" })}>
              {parcelNo && <Trans i18n={i18n!} i18nKey="order-success-parcel_no" values={{ number: parcelNo }} />}
            </Box>
          </Box>
          <Box>
            {pinInfo}
            {pickupInfo}
          </Box>
        </Flex>
      </Box>
    </HStack>
  );
};
export default OrderInfo;
